import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ContactForm } from "./ContactForm";
import { MapPin } from "lucide-react";

// One of Alina's real in-store working photos, used as a soft transparent
// backdrop behind the hero copy (the other in-store photo lives in "Il Metodo").
const HERO_BG =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_valorizza-stile/artifacts/idvz59dj_WhatsApp%20Image%202026-02-13%20at%2016.08.42.jpeg";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yTitle = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const line = {
    hidden: { y: "115%" },
    show: (i) => ({
      y: "0%",
      transition: { duration: 1, delay: 0.35 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen pt-28 sm:pt-32 pb-16 overflow-hidden"
      data-testid="hero-section"
    >

      {/* Alina at work — transparent backdrop behind the hero copy */}
      <div
        aria-hidden="true"
        data-testid="hero-bg-photo"
        className="pointer-events-none absolute inset-0 z-0 opacity-40 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      {/* Soft light scrim to keep the copy fully legible over the photo */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-r from-crema/80 via-crema/45 to-crema/25"
      />

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center relative z-10">
        {/* Left: kinetic title */}
        <motion.div style={{ y: yTitle, opacity }}>
          <h1 className="font-display uppercase text-fuchsiabrand tracking-tight text-4xl sm:text-6xl lg:text-[4.3vw] leading-[1.03]">
            <span className="reveal-mask">
              <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">
                Non ti servono
              </motion.span>
            </span>
            <span className="reveal-mask">
              <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block">
                più <span className="text-espresso">vestiti:</span>
              </motion.span>
            </span>
            <span className="reveal-mask">
              <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block">
                ti serve
              </motion.span>
            </span>
            <span className="reveal-mask">
              <motion.span custom={3} variants={line} initial="hidden" animate="show" className="block">
                un <span className="text-espresso">metodo.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            className="mt-8 text-lg sm:text-xl text-espresso font-medium max-w-xl leading-relaxed"
          >
            Styling creativo e shopping strategico per valorizzare il tuo guardaroba.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-7 flex items-start gap-2 text-espresso"
          >
            <MapPin size={14} className="text-fuchsiabrand flex-shrink-0 mt-[3px]" />
            <p className="font-mono text-[11px] sm:text-xs tracking-wide leading-relaxed uppercase text-espresso font-medium max-w-md">
              Bergamo, Lombardia e su richiesta in tutta Italia — Consulenze esclusivamente dal vivo.
            </p>
          </motion.div>
        </motion.div>

        {/* Right: form */}
        <div className="w-full">
          <ContactForm />
        </div>
      </div>
    </section>
  );
};
